import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isToday} from '../utils/dateTime';
import {relativeDayRu} from '../utils/date';
import {Link, NavLink, Outlet, useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import AuthImage from '../components/AuthImage';
import Spinner from '../components/Spinner';
import ManageListsModal from '../components/ManageListsModal';
import ConfirmDialog from '../components/ConfirmDialog';
import type {VisualProfileData} from '../components/ItemCompatibilityAttributes';
import {hasPermission, useWardrobe} from '../context/WardrobeContext';
import {
    DisposalIntent,
    INTENT_OPTIONS,
    ItemLifecycleStatus,
    ItemPurpose,
    LIFECYCLE_LABELS,
    PURPOSE_LABELS,
    ItemAvailability,
    availabilityLabel,
    availabilityRestricted,
    itemStatusLabel,
    pendingDisposalLabel,
    retirementLabel,
    wearContextLabel,
} from '../support/itemLifecycle';
import {useToast} from '../context/ToastContext';
import notificationStore from '../store/NotificationStore';
import {hasWardrobeSearchContext, readWardrobeSession, wardrobeReturnQuery} from '../support/wardrobeSession';
import IconButton from "../components/IconButton";
import {
    ArrowLeftIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    DisposalIcon,
    EllipsisVerticalIcon,
    HangerIcon,
    ListIcon,
    PencilIcon,
    RefreshIcon,
    SparklesIcon,
    TrashIcon,
    XIcon,
} from '../components/icons';
import {fetchRef} from '../service/refsCache';
import {slotForCategoryCode} from '../components/outfit-builder/steps';
import {pluralTimes} from '../utils/plural';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

// ─── Types ─────────────────────────────────────────────────────────────────

interface ClothingListBrief {
    id: string;
    name: string;
}

export interface ClothingItemData {
    id: string;
    name: string;
    lists?: ClothingListBrief[];
    description: string | null;
    /** Additive-поля DTO: нужны для слота конструктора и ссылки на каталог по бренду. */
    categoryId?: string | null;
    categoryName: string | null;
    brandId?: string | null;
    brandName: string | null;
    sizeName: string | null;
    conditionName: string | null;
    colorNames: string | null;
    layerNames: string | null;
    warmthLevelName: string | null;
    priceRub: number | null;
    waterProof: boolean | null;
    purchaseDate: string | null;
    disposalDate: string | null;
    thrownAway: boolean | null;
    inTrashBin: boolean | null;
    purpose: ItemPurpose | null;
    lifecycleStatus: ItemLifecycleStatus | null;
    /** Единый вычисляемый статус доступности вещи (D14): приходит из DTO готовым. */
    availability?: ItemAvailability | null;
    availabilityLabel?: string | null;
    disposalIntent: DisposalIntent | null;
    seasonNames: string | null;
    materialNames: string | null;
    occasionNames: string | null;
    styleNames: string | null;
    weatherSuitabilityNames: string | null;
    defectNames: string | null;
    printTypeName: string | null;
    fitName: string | null;
    formalityName: string | null;
    photoUrls: string[] | null;
    rating: number | null;
    totalWears: number | null;
    totalWashes: number | null;
    createdAt: string | null;
    updatedAt: string | null;
    aiUpdatedAt: string | null;
    lastWornAt: string | null;
    /** item-attribute-vector (FR-5): числовой вектор сочетаемости; null = ещё не рассчитан. */
    visualProfile?: VisualProfileData | null;
}

export interface AiSummaryData {
    shortSummary: string | null;
    itemAdvice: string | null;
    status: string | null;
    aiRatingOverall: number | null;
    occasionRatingDaily: number | null;
    occasionRatingOffice: number | null;
    occasionRatingSport: number | null;
    occasionRatingEvening: number | null;
    occasionRatingOutdoor: number | null;
}

export interface ItemPageContext {
    item: ClothingItemData;
    itemId: string;
    selectedOwnerId: string | null;
    aiProfile: AiSummaryData | null;
    /** Перезагрузка вещи (ai-advice-ladder: после CTA по совету статус вещи меняется). */
    refreshItem: () => void;
    /** Право EDIT_ITEMS в этом гардеробе и активная вещь — можно отменять носки (QA-8). */
    canEdit: boolean;
    /**
     * Слот конструктора для категории этой вещи (CTHS-315); `null` — категория не ложится ни в
     * один слот. Нужен вкладкам, которые отправляют вещь в конструктор вместе с другими
     * (блок «Лучшие сочетания», CTHS-135).
     */
    builderSlot: string | null;
}

/** Ответ POST /clothing/items/{id}/wear (WearRecordResultDto). */
interface WearRecordResult {
    eventId?: string;
    alreadyWornToday?: boolean;
    item?: Partial<ClothingItemData>;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Относительная подпись даты живёт в utils/date (Q11): раньше «Сегодня» означало «меньше
// суток назад», и вчерашняя вечерняя носка подписывалась «Сегодня», хотя блок
// «Использование» тут же показывал вчерашнее число. Теперь сутки календарные, а дни
// склоняются («2 дня назад», а не «2 дней назад»).

const COLOR_MAP: Record<string, string> = {
    белый: '#f8fafc',
    чёрный: '#18181b',
    серый: '#6b7280',
    синий: '#2563eb',
    красный: '#dc2626',
    зелёный: '#16a34a',
    жёлтый: '#eab308',
    оранжевый: '#f97316',
    розовый: '#ec4899',
    фиолетовый: '#7c3aed',
    коричневый: '#92400e',
    бежевый: '#d4b896',
    голубой: '#38bdf8',
    бордовый: '#881337',
    хаки: '#84895a',
};

// Пункты кебаб-меню: тач-зона ≥44px (styleguide, мобильная адаптация), dark/light.
const MENU_ITEM_CLASS = 'w-full px-4 py-2 min-h-[44px] text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2';
// Пункт вложенного уровня («Жизненный цикл») — отступ слева поверх базового стиля.
const SUBMENU_ITEM_CLASS = `${MENU_ITEM_CLASS} pl-8`;
// Пункт второго вложенного уровня («Избавиться…»).
const SUBSUBMENU_ITEM_CLASS = `${MENU_ITEM_CLASS} pl-12`;

// ─── Sub-components ────────────────────────────────────────────────────────

const SidebarRow: React.FC<{label: string; value: React.ReactNode}> = ({label, value}) => (
    <div className="flex justify-between gap-2">
        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 text-sm">{label}</span>
        <span className="text-gray-900 dark:text-gray-100 text-right text-sm">{value}</span>
    </div>
);

const ColorDots: React.FC<{colorNames: string}> = ({colorNames}) => {
    const names = colorNames.split(/[,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    return (
        <span className="flex items-center gap-1 justify-end flex-wrap">
            {names.map((name, i) => (
                <span
                    key={i}
                    className="inline-block rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0"
                    style={{
                        width: 10,
                        height: 10,
                        backgroundColor: COLOR_MAP[name] ?? '#6b7280',
                    }}
                    title={name}
                />
            ))}
            <span>{colorNames}</span>
        </span>
    );
};

// ─── Tabs config ───────────────────────────────────────────────────────────

const TABS = [
    {key: 'details',  label: 'Обзор'},
    {key: 'outfits',  label: 'Образы'},
    {key: 'history',  label: 'История'},
    {key: 'comments', label: 'Комментарии'},
    {key: 'ai',       label: 'AI профиль'},
] as const;

// ─── Main component ────────────────────────────────────────────────────────

const ClothingItemDetail: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {showToast} = useToast();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();
    const canEditPermission = isOwnWardrobe || hasPermission(permissions, 'EDIT_ITEMS');
    const canManageListItemsPermission = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LIST_ITEMS');
    const canMoveToTrash = isOwnWardrobe || hasPermission(permissions, 'MOVE_TO_TRASH');

    const [item, setItem] = useState<ClothingItemData | null>(null);
    const [aiProfile, setAiProfile] = useState<AiSummaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [manageListsOpen, setManageListsOpen] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Вложенные уровни кебаб-меню: «Жизненный цикл» и внутри него «Избавиться…».
    const [lifecycleOpen, setLifecycleOpen] = useState(false);
    const [disposeOpen, setDisposeOpen] = useState(false);
    const [wearDropdownOpen, setWearDropdownOpen] = useState(false);

    const moreMenuRef = useRef<HTMLDivElement>(null);
    const wearDropdownRef = useRef<HTMLDivElement>(null);

    // Вещь в корзине или выбывшая живёт только в режиме чтения (retired-item-actions-guard):
    // носка, правка, перевод в домашние/рабочие, AI-анализ, списки и комментарии ей недоступны —
    // на бэкенде это 409, здесь просто не показываем действия, которых всё равно не будет.
    const isFrozen = !!item?.inTrashBin || !!item?.thrownAway;
    const canEdit = canEditPermission && !isFrozen;
    const canManageListItems = canManageListItemsPermission && !isFrozen;
    // Одна носка в сутки (QA-8): кнопка честно говорит, что за сегодня уже отмечено.
    const wornToday = isToday(item?.lastWornAt);

    // ── Возврат в каталог (QA-7) ───────────────────────────────────────────
    // Снапшот каталога читаем один раз на карточку: «Все вещи» ведёт в /clothing с
    // сохранённым поиском/сортировкой, а при активном отборе рядом есть явная
    // кнопка «Назад к результатам».
    const wardrobeSession = useMemo(() => readWardrobeSession(), []);
    const wardrobeReturnPath = `/clothing${wardrobeReturnQuery(wardrobeSession)}`;
    const hasSearchContext = hasWardrobeSearchContext(wardrobeSession);

    // ── Fetch item ─────────────────────────────────────────────────────────

    const fetchItem = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ClothingItemData>(`/clothing/items/${id}`);
            setItem(res.data);
            setPhotoIndex(0);
        } catch {
            setError('Не удалось загрузить данные');
            setItem(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchItem();
    }, [id, fetchItem]);

    // ── Fetch AI profile (non-blocking) ────────────────────────────────────

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<AiSummaryData>(`/clothing/${id}/ai-profile`);
                if (!cancelled) setAiProfile(res.data);
            } catch {
                if (!cancelled) setAiProfile(null);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    // ── Document title ─────────────────────────────────────────────────────

    // Пока вещь не загружена — заголовок раздела («Все вещи»), потом имя вещи.
    useDocumentTitle(item?.name);

    // ── Keyboard navigation for photo modal ────────────────────────────────

    const photos = item?.photoUrls ?? [];

    useEffect(() => {
        if (!photoModalOpen || photos.length === 0) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setPhotoModalOpen(false);
            else if (e.key === 'ArrowLeft') setPhotoIndex(i => (i - 1 + photos.length) % photos.length);
            else if (e.key === 'ArrowRight') setPhotoIndex(i => (i + 1) % photos.length);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [photoModalOpen, photos.length]);

    // ── Click outside to close dropdowns ──────────────────────────────────

    // Закрытие кебаб-меню сворачивает вложенные уровни — следующее открытие с верхнего уровня.
    useEffect(() => {
        if (!moreMenuOpen) {
            setLifecycleOpen(false);
            setDisposeOpen(false);
        }
    }, [moreMenuOpen]);

    useEffect(() => {
        if (!moreMenuOpen && !wearDropdownOpen) return;
        const onClickOutside = (e: MouseEvent) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
                setMoreMenuOpen(false);
            }
            if (wearDropdownRef.current && !wearDropdownRef.current.contains(e.target as Node)) {
                setWearDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [moreMenuOpen, wearDropdownOpen]);

    // ── Wear / Wash actions ────────────────────────────────────────────────

    /**
     * Отмена носки (QA-8): снимает событие и возвращает счётчики. Используется действием
     * «Отменить» в тосте сразу после отметки и повторной отметкой в тот же день.
     */
    const undoWear = useCallback(async (eventId: string) => {
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.delete<{totalWears?: number; lastWornAt?: string | null}>(
                `/wear-history/${eventId}`, {params});
            setItem(prev => prev ? {
                ...prev,
                totalWears: res.data?.totalWears ?? prev.totalWears,
                lastWornAt: res.data?.lastWornAt ?? null,
            } : prev);
            showToast('Носка отменена', 'success');
        } catch {
            showToast('Не удалось отменить носку', 'error');
        }
    }, [selectedOwnerId, showToast]);

    // Одна носка в сутки пользователя: повторный клик не создаёт событие, но всегда
    // предлагает отмену — иначе ошибочная отметка остаётся без видимой коррекции (QA-8).
    const recordWear = useCallback(async () => {
        if (!item?.id) return;
        try {
            const api = await createAxiosInstance();
            const res = await api.post<WearRecordResult>(`/clothing/items/${item.id}/wear`);
            const {item: updated, eventId, alreadyWornToday} = res.data ?? {};
            setItem(prev => prev ? {...prev, ...(updated ?? {})} : prev);
            const undo = eventId ? {label: 'Отменить', onClick: () => void undoWear(eventId)} : undefined;
            showToast(
                alreadyWornToday ? 'Уже отмечено сегодня' : 'Носка отмечена за сегодня',
                alreadyWornToday ? 'info' : 'success',
                undo,
            );
        } catch {
            showToast('Не удалось записать носку', 'error');
        }
    }, [item?.id, showToast, undoWear]);

    const recordWash = useCallback(async () => {
        if (!item?.id) return;
        setWearDropdownOpen(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.post(`/clothing/items/${item.id}/wash`);
            setItem(prev => prev ? {...prev, ...res.data} : prev);
            showToast('Стирка записана', 'success');
        } catch {
            showToast('Не удалось записать стирку', 'error');
        }
    }, [item?.id, showToast]);

    // ── Dispose actions ────────────────────────────────────────────────────

    // После перевода в корзину: карточка переходит на «Обзор» (там бейдж и статус),
    // колокольчик подтягивает новое уведомление, тост даёт «Отменить» (U6).
    const afterTrashed = useCallback(() => {
        fetchItem();
        void notificationStore.loadUnreadCount();
        navigate(`/clothing/${item?.id}/details`, {replace: true});
    }, [fetchItem, navigate, item?.id]);

    const restoreFromTrashBin = useCallback(async () => {
        if (!item?.id) return;
        setMoreMenuOpen(false);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${item.id}/restore`);
            showToast('Вещь возвращена в гардероб', 'success');
            fetchItem();
            void notificationStore.loadUnreadCount();
        } catch {
            showToast('Не удалось восстановить вещь', 'error');
        }
    }, [item?.id, fetchItem, showToast]);

    const moveToTrashBin = useCallback(async () => {
        if (!item?.id) return;
        setMoreMenuOpen(false);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${item.id}/trash`);
            showToast('Решение принято: вещь ждёт передачи', 'success', {label: 'Отменить', onClick: () => { void restoreFromTrashBin(); }});
            afterTrashed();
        } catch {
            showToast('Не удалось записать решение', 'error');
        }
    }, [item?.id, afterTrashed, restoreFromTrashBin, showToast]);

    // «Избавиться…»: способ + переход на стадию «Ждут передачи» (обратимо, confirm не нужен —
    // факт выбытия подтверждается отдельно, на своей стадии).
    const disposeItem = useCallback(async (intent: DisposalIntent) => {
        if (!item?.id) return;
        setMoreMenuOpen(false);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${item.id}/dispose`, {intent});
            showToast(`Вещь ждёт передачи: ${pendingDisposalLabel(intent)}`, 'success', {label: 'Отменить', onClick: () => { void restoreFromTrashBin(); }});
            afterTrashed();
        } catch {
            showToast('Не удалось записать решение по вещи', 'error');
        }
    }, [item?.id, afterTrashed, restoreFromTrashBin, showToast]);

    // ── Lifecycle / soft delete actions ───────────────────────────────────

    // Только активные переходы (MOVED_TO_*/IN_USE): выбытие идёт через «Избавиться…» + корзину.
    const changeLifecycle = useCallback(async (status: ItemLifecycleStatus) => {
        if (!item?.id) return;
        setMoreMenuOpen(false);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${item.id}/lifecycle`, {status});
            showToast(`Статус изменён: ${LIFECYCLE_LABELS[status]}`, 'success');
            fetchItem();
        } catch {
            showToast('Не удалось изменить статус вещи', 'error');
        }
    }, [item?.id, fetchItem, showToast]);

    // Удаление ошибочной записи подтверждается общим диалогом, а не нативным
    // `confirm()`: тот не знает темы, не читается как часть интерфейса и на мобильных
    // выглядит системным сбоем (U13).
    const deleteItem = useCallback(async () => {
        if (!item?.id) return;
        setDeleting(true);
        try {
            const api = await createAxiosInstance();
            await api.delete(`/clothing/items/${item.id}`);
            showToast('Вещь удалена', 'success');
            navigate('/clothing');
        } catch {
            showToast('Не удалось удалить вещь', 'error');
        } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    }, [item?.id, navigate, showToast]);

    // ── AI re-recognition ──────────────────────────────────────────────────

    // item-reanalysis-dialog (FR-1): пункт меню — ярлык к единственной реализации потока,
    // диалогу во вкладке AI. Раньше он сразу создавал заявку, списывал квоту и уводил на
    // общий список распознаваний — пользователь не успевал понять, что запустил ИИ.
    const openReanalysis = useCallback(() => {
        if (!item?.id) return;
        setMoreMenuOpen(false);
        navigate(`/clothing/${item.id}/ai?reanalyze=1`);
    }, [item?.id, navigate]);

    // ── Condition badge ────────────────────────────────────────────────────

    // R7: бейджи «В корзине»/«Выброшено» не зависят от наличия состояния вещи —
    // раньше без conditionName вещь в корзине выглядела рабочей.
    const getConditionBadge = () => {
        if (!item) return null;
        const isDisposed = !!item.thrownAway;
        const isTrash = !!item.inTrashBin && !item.thrownAway;
        if (isDisposed) {
            return (
                <span data-testid="item-status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    <DisposalIcon className="w-4 h-4"/>
                    {retirementLabel(item.lifecycleStatus) ?? 'Выброшено'}
                </span>
            );
        }
        if (isTrash) {
            const pending = pendingDisposalLabel(item.disposalIntent);
            return (
                <span data-testid="item-status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                    <TrashIcon className="w-4 h-4"/>
                    {pending ? `Ждёт передачи · ${pending}` : 'Ждёт передачи'}
                </span>
            );
        }
        if (!item.conditionName) return null;
        return (
            <span className="px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
                {item.conditionName}
            </span>
        );
    };

    // ── «Собрать образ» (CTHS-315) ─────────────────────────────────────────
    //
    // Конструктор принимает состав через location.state.prefillItems со слотами, поэтому
    // здесь нужно превратить категорию вещи в слот образа. Код категории берётся из
    // кэшированного справочника: в DTO карточки есть только categoryId.

    const [builderSlot, setBuilderSlot] = useState<string | null>(null);

    useEffect(() => {
        const categoryId = item?.categoryId;
        if (!categoryId) {
            setBuilderSlot(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const cats = await fetchRef<{id: string; code: string}[]>('categories');
                if (cancelled) return;
                const code = cats.find(c => c.id === categoryId)?.code ?? null;
                setBuilderSlot(slotForCategoryCode(code));
            } catch {
                if (!cancelled) setBuilderSlot(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [item?.categoryId]);

    /**
     * CTHS-313/291: автосборка образа вокруг этой вещи. Запрос уходит на странице образа
     * (там же живут состояния loading/error и все действия с образом), сюда попадает только
     * переход с якорем в адресе — ссылку можно перезагрузить и переслать.
     */
    const startAutoOutfit = () => {
        if (!item) return;
        navigate(`/outfit?seedItemId=${item.id}`);
    };

    const startOutfitBuilder = () => {
        if (!item || !builderSlot) return;
        navigate('/outfits/builder', {
            state: {
                prefillItems: [{
                    id: item.id,
                    name: item.name,
                    brandName: item.brandName,
                    primaryPhotoUrl: item.photoUrls?.[0] ?? null,
                    slot: builderSlot,
                    categoryId: item.categoryId,
                }],
            },
        });
    };

    // ── Sidebar property rows ──────────────────────────────────────────────

    const renderSidebarRows = () => {
        if (!item) return null;
        const rows: React.ReactNode[] = [];

        if (item.purpose) {
            rows.push(<SidebarRow key="purpose" label="Назначение" value={PURPOSE_LABELS[item.purpose] ?? item.purpose}/>);
        }
        // R7: корзина/выбытие важнее lifecycle; «Используется» в сайдбаре не дублируем.
        const status = itemStatusLabel(item);
        if (status && status !== LIFECYCLE_LABELS.IN_USE) {
            rows.push(<SidebarRow key="lifecycle" label="Статус" value={status}/>);
        }
        // D14: «Доступность» — тот же ответ, что дают подбор образа и рекомендации. Раньше
        // карточка молчала, и вещь могла быть «непригодной» в ленте и «совместимой» в образе.
        const availability = availabilityLabel(item.availability, item.availabilityLabel);
        if (availability && availabilityRestricted(item.availability)) {
            rows.push(<SidebarRow key="availability" label="Доступность" value={
                <span data-testid="item-availability">{availability}</span>
            }/>);
        }
        if (item.brandName) {
            // CTHS-258: бренд ведёт в каталог с уже наложенным фильтром — имена параметров
            // берутся из support/catalogFilters (`brandIds`).
            rows.push(<SidebarRow key="brand" label="Бренд" value={item.brandId ? (
                <Link
                    to={`/clothing?brandIds=${item.brandId}`}
                    data-testid="item-brand-link"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                    {item.brandName}
                </Link>
            ) : item.brandName}/>);
        }
        if (item.sizeName) {
            rows.push(<SidebarRow key="size" label="Размер" value={item.sizeName}/>);
        }
        if (item.fitName) {
            rows.push(<SidebarRow key="fit" label="Крой" value={item.fitName}/>);
        }
        if (item.colorNames) {
            rows.push(<SidebarRow key="color" label="Цвет" value={<ColorDots colorNames={item.colorNames}/>}/>);
        }
        if (item.seasonNames) {
            rows.push(<SidebarRow key="seasons" label="Сезоны" value={item.seasonNames}/>);
        }
        if (item.styleNames) {
            rows.push(<SidebarRow key="style" label="Стиль" value={item.styleNames}/>);
        }
        if (item.materialNames) {
            rows.push(<SidebarRow key="material" label="Материал" value={item.materialNames}/>);
        }
        if (item.occasionNames) {
            rows.push(<SidebarRow key="occasions" label="Поводы" value={item.occasionNames}/>);
        }
        if (item.priceRub != null) {
            rows.push(<SidebarRow key="price" label="Цена" value={`${item.priceRub} ₽`}/>);
        }
        return rows;
    };

    // ──────────────────────────────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            {/* ── Breadcrumb ─────────────────────────────────────────────── */}
            <nav aria-label="Хлебные крошки" className="flex items-center gap-2 text-sm flex-wrap">
                <button
                    type="button"
                    data-testid="breadcrumb-all-items"
                    onClick={() => navigate(wardrobeReturnPath)}
                    className="min-h-[44px] lg:min-h-0 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                    Все вещи
                </button>
                <span className="text-gray-400 dark:text-gray-600">›</span>
                <span className="text-gray-900 dark:text-gray-100 truncate max-w-xs">
                    {item?.name ?? '...'}
                </span>
                {hasSearchContext && (
                    <button
                        type="button"
                        data-testid="breadcrumb-back-to-results"
                        onClick={() => navigate(wardrobeReturnPath)}
                        className="ml-auto inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 px-2 rounded-lg
                                   text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                    >
                        <ArrowLeftIcon className="w-4 h-4"/>
                        Назад к результатам
                    </button>
                )}
            </nav>

            {loading ? (
                <div className="flex justify-center py-12"><Spinner/></div>
            ) : error ? (
                <div className="p-4 text-center text-red-600 dark:text-red-400">{error}</div>
            ) : item ? (
                <div className="space-y-5">
                    {/* ── Action bar ─────────────────────────────────────── */}
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                        {/* CTHS-313: автосборка образа вокруг этой вещи (вещь закреплена) */}
                        {!isFrozen && (
                            <button
                                type="button"
                                data-testid="item-autopick-outfit-btn"
                                onClick={startAutoOutfit}
                                className="btn-secondary py-2 px-4 flex items-center gap-1.5"
                                title="AI подберёт образ, оставив эту вещь в составе"
                            >
                                <SparklesIcon className="w-4 h-4"/>
                                Подобрать образ
                            </button>
                        )}
                        {/* CTHS-315: собрать образ вручную, начиная с этой вещи */}
                        {builderSlot && !isFrozen && (
                            <button
                                type="button"
                                data-testid="item-build-outfit-btn"
                                onClick={startOutfitBuilder}
                                className="btn-secondary py-2 px-4 flex items-center gap-1.5"
                            >
                                <HangerIcon className="w-4 h-4"/>
                                Собрать образ
                            </button>
                        )}
                        {canEdit && (
                            <button
                                type="button"
                                data-testid="item-edit-btn"
                                onClick={() => navigate(`/clothing/${item.id}/edit`)}
                                className="btn-secondary py-2 px-4 flex items-center gap-1.5"
                            >
                                <PencilIcon className="w-4 h-4"/>
                                Редактировать
                            </button>
                        )}

                        {/* Wear split button */}
                        {canEdit && (
                            <div className="relative flex" ref={wearDropdownRef}>
                                <button
                                    onClick={recordWear}
                                    data-testid="item-wear-btn"
                                    className="btn-primary py-2 px-4 rounded-r-none"
                                >
                                    {wornToday ? 'Надето сегодня' : 'Надеть'}
                                </button>
                                <button
                                    onClick={() => setWearDropdownOpen(v => !v)}
                                    data-testid="item-wear-more-btn"
                                    aria-expanded={wearDropdownOpen}
                                    className="btn-primary py-2 px-2 rounded-l-none border-l border-white/20"
                                    aria-label="Дополнительные действия носки"
                                >
                                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${wearDropdownOpen ? 'rotate-180' : ''}`}/>
                                </button>
                                {wearDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-1 z-30 min-w-[140px] py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                        <button
                                            onClick={recordWash}
                                            data-testid="item-wash-btn"
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            Стирал
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* More (…) menu */}
                        <div className="relative" ref={moreMenuRef}>
                            <button
                                onClick={() => setMoreMenuOpen(v => !v)}
                                data-testid="item-actions-menu-btn"
                                aria-expanded={moreMenuOpen}
                                className="btn-secondary py-2 px-2"
                                aria-label="Ещё действия"
                            >
                                <EllipsisVerticalIcon className="w-5 h-5"/>
                            </button>
                            {moreMenuOpen && (
                                <div data-testid="item-actions-menu"
                                     className="absolute right-0 top-full mt-1 z-30 min-w-[240px] py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                    {canEdit && (
                                        <button
                                            onClick={openReanalysis}
                                            data-testid="item-reanalysis-menu"
                                            className={MENU_ITEM_CLASS}
                                        >
                                            <SparklesIcon className="w-4 h-4"/>
                                            Повторный ИИ-анализ
                                        </button>
                                    )}
                                    {canManageListItems && (
                                        <button
                                            onClick={() => { setMoreMenuOpen(false); setManageListsOpen(true); }}
                                            data-testid="item-manage-lists-menu"
                                            className={MENU_ITEM_CLASS}
                                        >
                                            <ListIcon className="w-4 h-4"/>
                                            Управление списками
                                        </button>
                                    )}
                                    {!item.thrownAway && canMoveToTrash && !item.inTrashBin && (
                                        <button onClick={moveToTrashBin} data-testid="item-move-to-trash-menu"
                                                className={MENU_ITEM_CLASS}>
                                            <TrashIcon className="w-4 h-4"/>
                                            Решить расстаться
                                        </button>
                                    )}
                                    {!item.thrownAway && canMoveToTrash && item.inTrashBin && (
                                        <button onClick={restoreFromTrashBin} data-testid="item-restore-menu"
                                                className={MENU_ITEM_CLASS}>
                                            <TrashIcon className="w-4 h-4"/>
                                            Вернуть в гардероб
                                        </button>
                                    )}
                                    {/* Подменю «Жизненный цикл»: переходы purpose (EDIT_ITEMS) и
                                        «Избавиться…» — способ + ожидание передачи (MOVE_TO_TRASH). */}
                                    {!item.thrownAway && (canEdit || canMoveToTrash) && (
                                        <>
                                            <div className="my-1 border-t border-gray-200 dark:border-gray-700"/>
                                            <button
                                                onClick={() => { setLifecycleOpen(v => !v); setDisposeOpen(false); }}
                                                data-testid="item-lifecycle-menu"
                                                aria-expanded={lifecycleOpen}
                                                className={MENU_ITEM_CLASS}
                                            >
                                                <RefreshIcon className="w-4 h-4"/>
                                                <span className="flex-1">
                                                    Жизненный цикл
                                                    {wearContextLabel(item.lifecycleStatus) && (
                                                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                            Сейчас: {wearContextLabel(item.lifecycleStatus)}
                                                        </span>
                                                    )}
                                                </span>
                                                <ChevronRightIcon className={`w-4 h-4 transition-transform ${lifecycleOpen ? 'rotate-90' : ''}`}/>
                                            </button>
                                            {lifecycleOpen && (
                                                <div className="bg-gray-50 dark:bg-gray-900/40">
                                                    {canEdit && item.lifecycleStatus !== 'MOVED_TO_HOME' && (
                                                        <button
                                                            onClick={() => changeLifecycle('MOVED_TO_HOME')}
                                                            data-testid="item-lifecycle-moved-to-home"
                                                            className={SUBMENU_ITEM_CLASS}
                                                        >
                                                            Перевести в домашние
                                                        </button>
                                                    )}
                                                    {canEdit && item.lifecycleStatus !== 'MOVED_TO_WORK' && (
                                                        <button
                                                            onClick={() => changeLifecycle('MOVED_TO_WORK')}
                                                            data-testid="item-lifecycle-moved-to-work"
                                                            className={SUBMENU_ITEM_CLASS}
                                                        >
                                                            Перевести в рабочие
                                                        </button>
                                                    )}
                                                    {canEdit && (item.lifecycleStatus === 'MOVED_TO_HOME' || item.lifecycleStatus === 'MOVED_TO_WORK') && (
                                                        <button
                                                            onClick={() => changeLifecycle('IN_USE')}
                                                            data-testid="item-lifecycle-in-use"
                                                            className={SUBMENU_ITEM_CLASS}
                                                        >
                                                            Вернуть в используемые
                                                        </button>
                                                    )}
                                                    {canMoveToTrash && (
                                                        <>
                                                            <button
                                                                onClick={() => setDisposeOpen(v => !v)}
                                                                data-testid="item-dispose-menu"
                                                                aria-expanded={disposeOpen}
                                                                className={SUBMENU_ITEM_CLASS}
                                                            >
                                                                <DisposalIcon className="w-4 h-4"/>
                                                                <span className="flex-1">Избавиться…</span>
                                                                <ChevronRightIcon className={`w-4 h-4 transition-transform ${disposeOpen ? 'rotate-90' : ''}`}/>
                                                            </button>
                                                            {disposeOpen && INTENT_OPTIONS.map(option => (
                                                                <button
                                                                    key={option.value}
                                                                    data-testid={`item-dispose-${option.value.toLowerCase()}`}
                                                                    onClick={() => disposeItem(option.value)}
                                                                    className={SUBSUBMENU_ITEM_CLASS}
                                                                >
                                                                    {option.label}
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {isOwnWardrobe && (
                                        <>
                                            <div className="my-1 border-t border-gray-200 dark:border-gray-700"/>
                                            <button
                                                onClick={() => {
                                                    setMoreMenuOpen(false);
                                                    setConfirmDelete(true);
                                                }}
                                                data-testid="item-delete-menu"
                                                className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                            >
                                                <XIcon className="w-4 h-4"/>
                                                Удалить (ошибочная запись)
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Three-column layout ────────────────────────────── */}
                    <div className="flex flex-col lg:flex-row gap-6">

                        {/* ── Left: Photo carousel ───────────────────────── */}
                        <div className="order-first lg:order-none flex-shrink-0 w-full lg:w-72 xl:w-80">
                            {photos.length > 0 ? (
                                <div className="relative">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setPhotoModalOpen(true)}
                                        onKeyDown={e => e.key === 'Enter' && setPhotoModalOpen(true)}
                                        className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden relative cursor-pointer"
                                    >
                                        <AuthImage
                                            imageKey={photos[photoIndex]}
                                            alt={item.name}
                                            variant="medium"
                                            className="w-full h-full object-contain"
                                        />
                                        {item.thrownAway && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800/90 text-white text-lg font-medium">
                                                    <DisposalIcon className="w-6 h-6 flex-shrink-0"/>
                                                    {retirementLabel(item.lifecycleStatus) ?? 'Выброшено'}
                                                </div>
                                            </div>
                                        )}
                                        {photos.length > 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={e => { e.stopPropagation(); setPhotoIndex(i => (i - 1 + photos.length) % photos.length); }}
                                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                                                    aria-label="Предыдущее фото"
                                                >‹</button>
                                                <button
                                                    type="button"
                                                    onClick={e => { e.stopPropagation(); setPhotoIndex(i => (i + 1) % photos.length); }}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                                                    aria-label="Следующее фото"
                                                >›</button>
                                            </>
                                        )}
                                        {/* Counter overlay bottom-left */}
                                        {photos.length > 1 && (
                                            <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">
                                                📷 {photoIndex + 1} / {photos.length}
                                            </div>
                                        )}
                                        {/* Expand icon bottom-right */}
                                        <div className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/40 text-white pointer-events-none">
                                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
                                    Нет фото
                                </div>
                            )}
                        </div>

                        {/* ── Center: Main info ──────────────────────────── */}
                        <div className="flex-1 min-w-0 space-y-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{item.name}</h1>
                            </div>

                            {/* Condition badge */}
                            {getConditionBadge()}

                            {/* Stats row */}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                                {item.totalWears != null && (
                                    <span>носил <strong className="text-gray-900 dark:text-gray-100">{pluralTimes(item.totalWears)}</strong></span>
                                )}
                                {item.lastWornAt && (
                                    <span>последняя носка: <strong className="text-gray-900 dark:text-gray-100">{relativeDayRu(item.lastWornAt)}</strong></span>
                                )}
                                {aiProfile?.aiRatingOverall != null && (
                                    <span>AI рейтинг <strong className="text-gray-900 dark:text-gray-100">{aiProfile.aiRatingOverall.toFixed(1)}</strong></span>
                                )}
                            </div>

                            {/* AI Summary card */}
                            {aiProfile?.status === 'COMPLETED' && aiProfile.shortSummary && (
                                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3 space-y-2">
                                    <div className="flex items-center gap-1.5 text-sm font-medium text-blue-700 dark:text-blue-300">
                                        <SparklesIcon className="w-4 h-4"/>
                                        AI-анализ
                                    </div>
                                    <p className="text-sm text-blue-800 dark:text-blue-200">{aiProfile.shortSummary}</p>
                                </div>
                            )}

                            {/* Lists */}
                            {(item.lists?.length ?? 0) > 0 && (
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">В списках:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {item.lists!.map(l => (
                                            <button
                                                key={l.id}
                                                onClick={() => navigate(`/lists/${l.id}`)}
                                                className="px-2 py-1 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                                            >
                                                {l.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Right: Properties sidebar ──────────────────── */}
                        <div className="flex-shrink-0 w-full lg:w-48 xl:w-56 space-y-2">
                            {renderSidebarRows()}
                        </div>
                    </div>

                    {/* ── Tab navigation ─────────────────────────────────── */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                        <div data-testid="item-tabs"
                             className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-5 overflow-x-auto">
                            {TABS.map(tab => (
                                <NavLink
                                    key={tab.key}
                                    to={`/clothing/${id}/${tab.key}`}
                                    data-testid={`item-tab-${tab.key}`}
                                    className={({isActive}) =>
                                        `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                                            isActive
                                                ? 'border-primary text-primary dark:text-primary-light'
                                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                        }`
                                    }
                                >
                                    {tab.label}
                                </NavLink>
                            ))}
                        </div>

                        <Outlet context={{item, itemId: id!, selectedOwnerId, aiProfile,
                            refreshItem: fetchItem, canEdit, builderSlot} satisfies ItemPageContext}/>
                    </div>
                </div>
            ) : null}

            {/* ── Photo lightbox ──────────────────────────────────────────── */}
            {photoModalOpen && item && photos.length > 0 && (
                <div
                    className="fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPhotoModalOpen(false)}
                >
                    <div
                        className="relative max-w-[90vw] max-h-[90vh] w-full flex items-center justify-center"
                        onClick={e => e.stopPropagation()}
                    >
                        <IconButton
                            label="Закрыть просмотр фото"
                            onClick={() => setPhotoModalOpen(false)}
                            className="absolute -top-10 right-0 rounded-full bg-white/10 text-white hover:bg-white/20 z-10"
                        >
                            <XIcon className="w-6 h-6"/>
                        </IconButton>
                        <div className="relative max-w-full max-h-[85vh] overflow-auto flex items-center justify-center">
                            <AuthImage
                                imageKey={photos[photoIndex]}
                                alt={item.name}
                                className="max-w-full max-h-[85vh] w-auto h-auto object-contain"
                            />
                            {item.thrownAway && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                                    <div className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-800/90 text-white text-xl font-medium">
                                        <DisposalIcon className="w-8 h-8 flex-shrink-0"/>
                                        Выброшено
                                    </div>
                                </div>
                            )}
                        </div>
                        {photos.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setPhotoIndex(i => (i - 1 + photos.length) % photos.length)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70"
                                >‹</button>
                                <button
                                    type="button"
                                    onClick={() => setPhotoIndex(i => (i + 1) % photos.length)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70"
                                >›</button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
                                    {photoIndex + 1} / {photos.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Manage lists modal ──────────────────────────────────────── */}
            {item && (
                <ManageListsModal
                    isOpen={manageListsOpen}
                    onClose={() => setManageListsOpen(false)}
                    itemId={item.id}
                    initialListIds={(item.lists ?? []).map(l => l.id)}
                    onSuccess={fetchItem}
                    ownerId={selectedOwnerId}
                />
            )}

            {/* ── Удаление ошибочной записи ───────────────────────────────── */}
            <ConfirmDialog
                open={confirmDelete}
                title="Удалить ошибочную запись?"
                description="Вещь исчезнет из гардероба и статистики безвозвратно. Если вы просто перестали её носить — примите решение расстаться, это обратимо."
                confirmLabel={deleting ? 'Удаляем…' : 'Удалить'}
                destructive
                busy={deleting}
                onConfirm={deleteItem}
                onCancel={() => setConfirmDelete(false)}
                data-testid="delete-item-dialog"
            />
        </div>
    );
};

export default ClothingItemDetail;
